import React from "react";
import { useMutation } from "@apollo/client";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// graphql
import GET_NAVBAR_INFO from "graphql/queries/GetNavbarInfo";
import GET_MY_CLASSES from "graphql/queries/GetMyClasses";
import BOOK_COURSE from "graphql/mutations/BookCourse";
import GET_COURSE_DETAILS from "graphql/queries/GetCourseDetails";

// common
import {
  ModalText,
  ModalLayout,
  LargeModalStyling,
  MoreModalButtons,
} from "common/modal";

const { LargeModal, LargeModalCreditInfo } = LargeModalStyling;
const { ConfirmationText, CreditWarning } = ModalText;
const { ModalRow } = ModalLayout;
const { ModalButtonYellow, ModalButtonBlue, ButtonContainer } =
  MoreModalButtons;
interface Topic {
  topic: {
    description: string;
    id: string;
    title: string;
  };
  startTime: string;
}

type BookCourseModalProps = {
  visible: boolean;
  onCloseModal: () => void;
  courseData: {
    id: string;
    title: string;
    remainingLessons: Topic[];
    remainingCourseCost: number;
    remainingStudentCredit: number;
    remainingAmountAfterBooking: number;
    bookable: boolean;
    overlappedLessons: Topic[];
    fullSeatLessons: Topic[];
    emptyIncomingLessons: Topic[];
  };
};

const BookCourseModal = (props: BookCourseModalProps) => {
  const { visible, onCloseModal, courseData } = props;

  const { t } = useTranslation("book_a_course");

  // BOOK COURSE
  const [bookCourse] = useMutation(BOOK_COURSE, {
    refetchQueries: [
      {
        query: GET_MY_CLASSES,
      },
      {
        query: GET_NAVBAR_INFO,
      },
      {
        query: GET_COURSE_DETAILS,
        variables: {
          id: courseData.id,
        },
      },
    ],
    onCompleted: () => {
      message.success(t("Course successfully booked!"));
    },
    onError: (e) => {
      message.error(e.graphQLErrors[0].message);
    },
  });

  const handleCreditsMessage = () => {
    if (courseData.bookable)
      return t(
        "To join this ongoing course is a great idea. There are {{ futureClasses }} class left.",
        {
          futureClasses: courseData.remainingLessons.length,
          count: courseData.remainingLessons.length,
        }
      ).concat(
        t(" To book all of them costs {{ cost }} credits.", {
          cost: courseData.remainingCourseCost,
          count: courseData.remainingCourseCost,
        })
      );
    return t("To book the full course costs {{ cost }} credits.", {
      cost: courseData.remainingCourseCost,
      count: courseData.remainingCourseCost,
    });
  };

  const renderDate = (d: string) => {
    const date = new Date(d);
    const year = date.getFullYear();
    const dates = date.getDate();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthName = months[date.getMonth()];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const weekdays = days[date.getDay()];
    return `${weekdays}, ${dates} ${monthName} ${year}`;
  };

  const fullCourseAvailability =
    courseData.overlappedLessons.length <= 0 &&
    courseData.fullSeatLessons.length <= 0 &&
    courseData.emptyIncomingLessons.length <= 0 &&
    courseData.remainingLessons.length !== 0;

  const renderNotAvailableToBookClasses = () => {
    const totalNotAvailableToBookClasses =
      courseData.overlappedLessons.length +
      courseData.fullSeatLessons.length +
      courseData.emptyIncomingLessons.length;
    return (
      <div>
        {t("You cannot book {{ class }} class ", {
          class: totalNotAvailableToBookClasses,
          count: totalNotAvailableToBookClasses,
        })}
        {courseData.overlappedLessons.length > 0 && (
          <span>
            {courseData.overlappedLessons?.map((topic: Topic) => (
              <span>
                <span style={{ fontWeight: "bold" }}>{topic.topic.title}</span>
                <span> on {renderDate(topic.startTime)} </span>
              </span>
            ))}
            <span> {t("(you have another class at this time)")}</span>
          </span>
        )}
        {courseData.fullSeatLessons.length > 0 && (
          <span>
            {courseData.fullSeatLessons?.map((topic: Topic) => (
              <span>
                <span style={{ fontWeight: "bold" }}>{topic.topic.title}</span>
                <span> on {renderDate(topic.startTime)} </span>
              </span>
            ))}
            <span> {t("(there are no seats left)")}</span>
          </span>
        )}
        {courseData.emptyIncomingLessons.length > 0 && (
          <span>
            {courseData.emptyIncomingLessons?.map((topic: Topic) => (
              <span>
                <span style={{ fontWeight: "bold" }}>{topic.topic.title}</span>
                <span> on {renderDate(topic.startTime)} </span>
              </span>
            ))}
            <span>
              {t(
                "(there are less than 48 hours remaining and the class is empty)"
              )}
            </span>
          </span>
        )}
        {t(
          ". All other future {{ class }} class in this course you can book. Do you want to book them?",
          {
            class: courseData.remainingLessons.length,
            count: courseData.remainingLessons.length,
          }
        )}
      </div>
    );
  };

  return (
    <LargeModal
      title={t("BOOK THIS COURSE")}
      centered
      visible={visible}
      footer={null}
      onCancel={onCloseModal}
    >
      <ConfirmationText>
        {fullCourseAvailability &&
          t("I want to book all future classes of this course.")}
        {!fullCourseAvailability && renderNotAvailableToBookClasses()}
      </ConfirmationText>
      <ModalRow gutter={24}>
        <ButtonContainer xs={20} sm={20} md={12}>
          <ModalButtonYellow
            type="primary"
            onClick={() => {
              bookCourse({
                variables: { courseId: courseData.id },
              });
              onCloseModal();
            }}
          >
            {t("CONFIRM")}
          </ModalButtonYellow>
        </ButtonContainer>
        <ButtonContainer xs={20} sm={20} md={12}>
          <ModalButtonBlue
            type="primary"
            onClick={() => {
              onCloseModal();
            }}
          >
            {t("CANCEL")}
          </ModalButtonBlue>
        </ButtonContainer>
      </ModalRow>
      <LargeModalCreditInfo>
        <span>
          {handleCreditsMessage()}{" "}
          {t("You currently have {{ credits }} credit left", {
            credits: courseData.remainingStudentCredit,
            count: courseData.remainingStudentCredit,
          })}
        </span>
        {courseData.remainingAmountAfterBooking < 0 ? (
          <CreditWarning>
            {t("You have insufficient credits.")}{" "}
            <Link to="/my-credits" style={{ textDecoration: "underline" }}>
              {t("Buy now")} &gt;
            </Link>
          </CreditWarning>
        ) : null}
        <span>
          {t(
            "You can cancel up to {{ hour }} hours before this class starts.",
            { hour: 24 }
          )}
        </span>
      </LargeModalCreditInfo>
    </LargeModal>
  );
};

export default BookCourseModal;
